/**
 * ============================================================================
 * SELECT COMPONENT - 100% Ready
 * ============================================================================
 */

import React, { forwardRef } from 'react';

/**
 * Select Component
 * @param {Object} props
 * @param {Array} props.options - Select options [{ value, label }]
 * @param {string} props.value - Selected value
 * @param {Function} props.onChange - Change handler
 * @param {string} props.placeholder - Placeholder text
 * @param {string} props.error - Error message
 * @param {string} props.className - Additional classes
 * @param {boolean} props.required - Required field
 * @param {string} props.label - Label text
 * @param {string} props.id - Select ID
 * @param {string} props.name - Select name
 * @returns {JSX.Element}
 */
const Select = forwardRef(
  (
    {
      options = [],
      value,
      onChange,
      placeholder = 'Select an option',
      error,
      className = '',
      required = false,
      label,
      id,
      name,
      ...props
    },
    ref
  ) => {
    const baseClasses = `
      w-full px-4 py-2.5
      border rounded-xl
      bg-white dark:bg-[rgba(255,255,255,0.05)]
      text-gray-900 dark:text-white
      placeholder-gray-400 dark:placeholder-gray-500
      focus:outline-none focus:ring-4 focus:ring-[rgba(34,197,94,0.2)] focus:border-[#22c55e]
      transition-all duration-200
      appearance-none
      bg-[url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%23a3a3a3'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")] 
      bg-[length:1.5em_1.5em] 
      bg-[right_0.75rem_center] 
      bg-no-repeat
      dark:bg-[url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%23a3a3a3'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")]
    `;

    const errorClasses = error
      ? 'border-red-500 focus:ring-red-500/20 focus:border-red-500'
      : 'border-gray-300 dark:border-[rgba(255,255,255,0.08)]';

    const classes = `${baseClasses} ${errorClasses} ${className}`.trim();

    const selectId = id || name;

    return (
      <div className="w-full">
        {label && (
          <label
            htmlFor={selectId}
            className="block text-sm font-medium text-gray-700 dark:text-white mb-2"
          >
            {label}
            {required && <span className="text-red-500 ml-1">*</span>}
          </label>
        )}
        <select
          ref={ref}
          id={selectId}
          name={name}
          value={value}
          onChange={onChange}
          required={required}
          className={classes}
          aria-invalid={!!error}
          aria-describedby={error ? `${selectId}-error` : undefined}
          {...props}
        >
          {placeholder && (
            <option value="" disabled>
              {placeholder}
            </option>
          )}
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        {error && (
          <p
            id={`${selectId}-error`}
            className="mt-1 text-sm text-red-600 dark:text-red-400"
            role="alert"
          >
            {error}
          </p>
        )}
      </div>
    );
  }
);

Select.displayName = 'Select';

export default Select;
